import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { RestaurantEditableCellShell } from './restaurant-editable-cell-shell'
import { restaurantFieldHighlightStyle, restaurantTableTooltipPanelStyle } from './restaurant-table-styles'
import { useRestaurantInlineEditor } from './use-restaurant-inline-editor'
import { TruncateTooltip } from '@/components/molecules/truncate-tooltip'
import { useAppDispatch } from '@/features/hooks'
import { saveMealMemo } from '@/features/restaurant-manager/restaurant-manager-thunks'
import { useErrorHandler } from '@/hooks/use-error-handler'
import type { RestaurantMealNoteView } from '@/models/restaurant-manager'

type Props = {
  reservationId: string
  mealNote: RestaurantMealNoteView | null
}

export const RestaurantMealNoteCell: React.FC<Props> = ({ reservationId, mealNote }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { errorHandler } = useErrorHandler()
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const memo = mealNote?.memo ?? null
  const [draftValue, setDraftValue] = useState(memo ?? '')
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const normalizeMealNoteValue = useCallback((value: string | null) => {
    const normalizedValue = value?.trim() ?? ''
    return normalizedValue === '' ? null : normalizedValue
  }, [])
  const normalizedMealNote = useMemo(() => normalizeMealNoteValue(memo), [memo, normalizeMealNoteValue])

  const closeEditor = useCallback(async () => {
    if (isSaving) {
      return
    }

    const nextMealNote = normalizeMealNoteValue(draftValue)
    if (nextMealNote === normalizedMealNote) {
      setIsEditing(false)
      return
    }

    try {
      setIsSaving(true)
      await dispatch(saveMealMemo({ reservationId, memo: draftValue.trim() })).unwrap()
      setIsEditing(false)
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsSaving(false)
    }
  }, [dispatch, draftValue, errorHandler, isSaving, normalizeMealNoteValue, normalizedMealNote, reservationId])

  const { containerRef, handleCompositionEnd, handleCompositionStart, handleEditorKeyDown } = useRestaurantInlineEditor({
    isEditing,
    isBusy: isSaving,
    onRequestClose: closeEditor,
    canCloseOnEnter: event => !event.shiftKey,
  })

  useEffect(() => {
    if (!isEditing) {
      setDraftValue(memo ?? '')
    }
  }, [isEditing, memo])

  useEffect(() => {
    if (!isEditing) {
      return
    }

    textareaRef.current?.focus()
    textareaRef.current?.select()
  }, [isEditing])

  const startEdit = useCallback(() => {
    if (isSaving) {
      return
    }

    setDraftValue(memo ?? '')
    setIsEditing(true)
  }, [isSaving, memo])

  return (
    <div
      ref={containerRef}
      css={cellContainerStyle}
      onCompositionStart={handleCompositionStart}
      onCompositionEnd={handleCompositionEnd}
      onKeyDown={isEditing ? handleEditorKeyDown : undefined}
    >
      <RestaurantEditableCellShell
        canEdit
        isEditing={isEditing}
        onStartEdit={startEdit}
        openOnClick
        editButtonAriaLabel={t('RestaurantManager.EditMealNote')}
        customStyle={shellStretchStyle}
      >
        {isEditing ? (
          <div css={editorFrameStyle}>
            <textarea
              ref={textareaRef}
              css={textareaStyle}
              value={draftValue}
              onChange={event => setDraftValue(event.target.value)}
              placeholder={t('RestaurantManager.EditMealNote')}
              disabled={isSaving}
              rows={4}
            />
          </div>
        ) : (
          <div css={[readonlyTextStyle, mealNote?.isHighlighted && restaurantFieldHighlightStyle]}>
            <TruncateTooltip text={memo} customPanelStyle={restaurantTableTooltipPanelStyle('mealNote')} />
          </div>
        )}
      </RestaurantEditableCellShell>
    </div>
  )
}

// .cell(flex) → cellContainer → shell → 省略表示 と高さを伝えることで、
// 行が他の列の都合で高くなっているときにその高さいっぱいまで表示できる。
// 途中を flex にして stretch で繋ぐ（パーセント高さで繋ぐと、伸びた高さが戻らなくなる）
const cellContainerStyle = css({
  alignSelf: 'stretch',
  display: 'flex',
  minWidth: 0,
  width: '100%',
})

const shellStretchStyle = css({
  display: 'flex',
})

const readonlyTextStyle = css({
  minWidth: 0,
  width: '100%',
})

const editorFrameStyle = css({
  background: '#FFFFFF',
  border: '1px solid #CCCCCC',
  borderRadius: 16,
  minHeight: 152,
  padding: 12,
  width: '100%',
})

const textareaStyle = css({
  background: '#FFFFFF',
  border: 'none',
  color: '#272727',
  fontSize: 14,
  lineHeight: '20px',
  minHeight: 126,
  outline: 'none',
  padding: 0,
  resize: 'none',
  width: '100%',
  '::placeholder': {
    color: '#999999',
  },
})
